import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  EXPRESSION_MAP,
  MOTION_PRESETS,
  TRANSIENT_REACTIONS,
  TransientReaction,
} from '@/config/buddyExpressions';
import { selectBuddyLayers, ALL_LAYER_ASSETS, BuddyLayers } from '@/config/buddyLayerMap';
import { selectBodyStage, BODY_STAGE_ASSETS, ALL_BODY_STAGE_ASSETS, BODY_STAGE_FACE_OFFSET } from '@/config/buddyBodyStage';
import { ComposedBuddyState, RecurringGesture } from '@/lib/state/baselineMoodComposer';
import { TransientReactionType } from '@/lib/types/events';
import { GESTURE_EFFECTS } from '@/lib/mappings/gestureProfileMap';
import {
  SignatureBehavior,
  SIGNATURE_BEHAVIORS,
} from '@/lib/state/signatureBehaviorMap';
import BuddyAura from './buddy/BuddyAura';
import BuddySvgEyes from './buddy/BuddySvgEyes';
import BuddySvgMouth from './buddy/BuddySvgMouth';

// ── Mood-based CSS filter presets ──
const MOOD_FILTERS: Record<string, string> = {
  calm:        'brightness(1.0) saturate(0.88) hue-rotate(0deg)',
  curious:     'brightness(1.03) saturate(1.0) hue-rotate(0deg)',
  alert:       'brightness(1.08) saturate(1.1) hue-rotate(-3deg)',
  stressed:    'brightness(0.88) saturate(1.2) hue-rotate(-10deg)',
  relieved:    'brightness(1.05) saturate(0.82) hue-rotate(6deg)',
  hyped:       'brightness(1.15) saturate(1.3) hue-rotate(0deg)',
  shaken:      'brightness(0.85) saturate(0.8) hue-rotate(-5deg)',
  vibing:      'brightness(1.0) saturate(0.92) hue-rotate(2deg)',
  celebrating: 'brightness(1.18) saturate(1.35) hue-rotate(2deg)',
};

interface BuddyCharacterProps {
  composed: ComposedBuddyState;
  activeSignature?: SignatureBehavior | null;
  heatLevel?: number;
  blockRelief?: boolean;
  statusOverride?: string;
  seasonState?: string;
  weather?: string;
  lightningVitality?: number;
  displayMacroState?: string | null;
  matrixMode?: boolean;
  enteringMatrix?: boolean;
  exitingMatrix?: boolean;
}

const TRANSIENT_KEY_MAP: Record<TransientReactionType, TransientReaction> = {
  blockFound: 'blockFound',
  feeSpike: 'feeSpike',
  pricePump: 'pricePump',
  priceDip: 'priceDip',
  longWait: 'longWait',
  backToBack: 'backToBack',
  volatilityShock: 'volatilityShock',
  fearSurge: 'fearSurge',
  greedPeak: 'greedPeak',
  feeRelief: 'feeRelief',
  droughtAnxiety: 'droughtAnxiety',
  halvingHype: 'halvingHype',
};

// ── Preload body layer assets on module load ──
if (typeof window !== 'undefined') {
  [...ALL_LAYER_ASSETS.filter(src => src.includes('body-')), ...ALL_BODY_STAGE_ASSETS].forEach(src => {
    const img = new Image();
    img.src = src;
  });
}

// ── Blink overlay component ──
const BlinkOverlay = React.memo(function BlinkOverlay({
  blinkInterval,
  blinkDuration,
  doubleBlink,
  faceOffset,
}: {
  blinkInterval: number;
  blinkDuration: number;
  doubleBlink: boolean;
  faceOffset: { svgTranslateY: number; svgScale: number; svgOriginX: number; svgOriginY: number };
}) {
  const [isBlinking, setIsBlinking] = useState(false);
  const blinkTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const scheduleBlink = () => {
      const interval = blinkInterval * (0.7 + Math.random() * 0.6);
      blinkTimer.current = setTimeout(() => {
        setIsBlinking(true);
        setTimeout(() => {
          setIsBlinking(false);
          if (doubleBlink && Math.random() > 0.5) {
            setTimeout(() => {
              setIsBlinking(true);
              setTimeout(() => setIsBlinking(false), blinkDuration * 0.7);
            }, 180);
          }
        }, blinkDuration);
        scheduleBlink();
      }, interval);
    };
    scheduleBlink();
    return () => { if (blinkTimer.current) clearTimeout(blinkTimer.current); };
  }, [blinkInterval, blinkDuration, doubleBlink]);

  return (
    <AnimatePresence>
      {isBlinking && (
        <motion.svg
          viewBox="0 0 400 500"
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 30 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.06 }}
        >
          <g transform={`translate(${faceOffset.svgOriginX}, ${faceOffset.svgOriginY}) scale(${faceOffset.svgScale}) translate(${-faceOffset.svgOriginX}, ${-faceOffset.svgOriginY}) translate(0, ${faceOffset.svgTranslateY})`}>
            <BuddySvgEyes variant="closed" />
          </g>
        </motion.svg>
      )}
    </AnimatePresence>
  );
});

// ── Layer image component — simple src swap with CSS opacity transition ──
// No AnimatePresence, no key remounting — just two persistent <img> that crossfade
const LayerImage = React.memo(function LayerImage({
  src,
  zIndex,
  duration = 0.25,
  style,
  priority = false,
}: {
  src: string;
  layerKey?: string;
  zIndex: number;
  duration?: number;
  style?: React.CSSProperties;
  priority?: boolean;
}) {
  const [slots, setSlots] = useState({ a: src, b: src, frontIsA: true });
  const currentRef = useRef(src);

  useEffect(() => {
    if (src !== currentRef.current) {
      currentRef.current = src;
      setSlots(prev => {
        if (prev.frontIsA) {
          return { a: prev.a, b: src, frontIsA: false };
        } else {
          return { a: src, b: prev.b, frontIsA: true };
        }
      });
    }
  }, [src]);

  const baseStyle: React.CSSProperties = {
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    zIndex,
    transition: `opacity ${duration}s ease`,
    ...style,
  };

  return (
    <>
      <img
        src={slots.a}
        alt=""
        className="absolute pointer-events-none select-none"
        draggable={false}
        fetchPriority={priority ? 'high' : undefined}
        style={{ ...baseStyle, opacity: slots.frontIsA ? 1 : 0 }}
      />
      <img
        src={slots.b}
        alt=""
        className="absolute pointer-events-none select-none"
        draggable={false}
        style={{ ...baseStyle, opacity: slots.frontIsA ? 0 : 1 }}
      />
    </>
  );
});

// ── Matrix glitch overlay — random slice/shift disruption ──
const MatrixGlitch = React.memo(function MatrixGlitch() {
  const [glitching, setGlitching] = useState(false);
  const [slices, setSlices] = useState<Array<{ top: number; height: number; tx: number; skew: number }>>([]);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const schedule = () => {
      const delay = 4000 + Math.random() * 8000; // 4-12s between glitches
      timer.current = setTimeout(() => {
        // Generate 3-6 random horizontal slices
        const count = 3 + Math.floor(Math.random() * 4);
        const newSlices = Array.from({ length: count }, () => {
          const top = Math.random() * 90;
          return {
            top,
            height: 3 + Math.random() * 12,
            tx: (Math.random() - 0.5) * 30,
            skew: (Math.random() - 0.5) * 8,
          };
        });
        setSlices(newSlices);
        setGlitching(true);

        // Hold glitch for 80-200ms then recover
        const hold = 80 + Math.random() * 120;
        setTimeout(() => {
          setGlitching(false);
          // Occasional double-glitch
          if (Math.random() > 0.6) {
            setTimeout(() => {
              setSlices(newSlices.map(s => ({ ...s, tx: -s.tx * 0.6 })));
              setGlitching(true);
              setTimeout(() => setGlitching(false), 60 + Math.random() * 80);
            }, 100 + Math.random() * 150);
          }
        }, hold);
        schedule();
      }, delay);
    };
    schedule();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, []);

  if (!glitching) return null;

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 50 }}>
      {/* RGB channel split — red/cyan offset copies */}
      <div
        className="absolute inset-0"
        style={{
          transform: 'translateX(3px)',
          opacity: 0.35,
          mixBlendMode: 'screen',
          filter: 'hue-rotate(-60deg) brightness(1.5)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          transform: 'translateX(-3px)',
          opacity: 0.25,
          mixBlendMode: 'screen',
          filter: 'hue-rotate(60deg) brightness(1.3)',
        }}
      />
      {/* Horizontal slice displacement bars */}
      {slices.map((s, i) => (
        <div
          key={i}
          className="absolute left-0 right-0"
          style={{
            top: `${s.top}%`,
            height: `${s.height}%`,
            background: `linear-gradient(90deg, transparent 10%, rgba(0,255,70,0.12) 30%, rgba(0,255,70,0.06) 70%, transparent 90%)`,
            transform: `translateX(${s.tx}px) skewX(${s.skew}deg)`,
            mixBlendMode: 'screen',
          }}
        />
      ))}
      {/* Brief white flash line */}
      <div
        className="absolute left-0 right-0"
        style={{
          top: `${slices[0]?.top ?? 50}%`,
          height: '1px',
          background: 'rgba(0,255,70,0.5)',
          boxShadow: '0 0 6px rgba(0,255,70,0.4)',
        }}
      />
    </div>
  );
});

function BuddyCharacterInner({
  composed, activeSignature, heatLevel = 0, blockRelief = false,
  statusOverride, seasonState = 'neutral', weather = 'clear',
  lightningVitality = 0.5, displayMacroState, matrixMode = false,
  enteringMatrix = false, exitingMatrix = false,
}: BuddyCharacterProps) {
  const { baseline, shortTermMood, activeTransient } = composed;
  const expression = EXPRESSION_MAP[shortTermMood];

  const [activeGesture, setActiveGesture] = useState<RecurringGesture | null>(null);
  const [signaturePhaseIndex, setSignaturePhaseIndex] = useState(0);
  const gestureTimer = useRef<ReturnType<typeof setTimeout>>();
  const signaturePhaseTimer = useRef<ReturnType<typeof setTimeout>>();

  const signatureConfig = activeSignature ? SIGNATURE_BEHAVIORS[activeSignature] : null;

  // ── Select 3-layer composition (eyes + mouth) ──
  const layers: BuddyLayers = useMemo(
    () => selectBuddyLayers(composed, activeSignature),
    [composed.shortTermMood, composed.activeTransient, activeSignature,
     composed.baseline.smileBias, composed.baseline.eyeTension, composed.baseline.postureBias]
  );

  // ── Body evolution stage (BittyB / BitGuard / Ragnarok-B) ──
  // Driven ONLY by long-term macro state with 60s hysteresis — no short-term mood/heat
  const bodyStage = useMemo(
    () => selectBodyStage(displayMacroState),
    [displayMacroState]
  );
  const bodyStageSrc = BODY_STAGE_ASSETS[bodyStage];
  const faceOffset = BODY_STAGE_FACE_OFFSET[bodyStage];

  // ── Evolution transition glow ──
  const prevStageRef = useRef(bodyStage);
  const [evolving, setEvolving] = useState(false);
  useEffect(() => {
    if (prevStageRef.current !== bodyStage) {
      prevStageRef.current = bodyStage;
      setEvolving(true);
      const t = setTimeout(() => setEvolving(false), 800);
      return () => clearTimeout(t);
    }
  }, [bodyStage]);

  // ── Signature behavior phase sequencer ──
  useEffect(() => {
    if (!signatureConfig) { setSignaturePhaseIndex(0); return; }
    setSignaturePhaseIndex(0);
    let currentPhase = 0;
    const advancePhase = () => {
      currentPhase++;
      if (currentPhase < signatureConfig.phases.length) {
        setSignaturePhaseIndex(currentPhase);
        signaturePhaseTimer.current = setTimeout(advancePhase, signatureConfig.phases[currentPhase].duration);
      }
    };
    if (signatureConfig.phases.length > 1) {
      signaturePhaseTimer.current = setTimeout(advancePhase, signatureConfig.phases[0].duration);
    }
    return () => { if (signaturePhaseTimer.current) clearTimeout(signaturePhaseTimer.current); };
  }, [activeSignature, signatureConfig]);

  // ── Recurring gestures ──
  useEffect(() => {
    if (!baseline.gestureSet.length || activeTransient || activeSignature) return;
    const scheduleGesture = () => {
      const jitter = baseline.gestureFrequency * (0.7 + Math.random() * 0.6);
      gestureTimer.current = setTimeout(() => {
        if (activeTransient || activeSignature) {
          gestureTimer.current = setTimeout(scheduleGesture, 2000);
          return;
        }
        const gesture = baseline.gestureSet[Math.floor(Math.random() * baseline.gestureSet.length)];
        setActiveGesture(gesture);
        const effect = GESTURE_EFFECTS[gesture];
        setTimeout(() => {
          setActiveGesture(null);
          gestureTimer.current = setTimeout(scheduleGesture, 1000);
        }, effect?.duration || 1500);
      }, jitter);
    };
    scheduleGesture();
    return () => { if (gestureTimer.current) clearTimeout(gestureTimer.current); };
  }, [baseline.gestureSet, baseline.gestureFrequency, activeTransient, activeSignature]);

  // ── Resolve transient config ──
  const transientConfig = activeTransient
    ? TRANSIENT_REACTIONS[TRANSIENT_KEY_MAP[activeTransient]]
    : null;

  // ── Resolve body animation ──
  const currentPhase = signatureConfig?.phases[signaturePhaseIndex];
  const signatureBodyAnim = (signatureConfig && !activeTransient && currentPhase)
    ? currentPhase.body : null;
  const phaseDurationMs = Number.isFinite(currentPhase?.duration)
    ? (currentPhase?.duration as number) : 400;
  const signatureTransition = (signatureConfig && currentPhase)
    ? { duration: Math.max(0.1, phaseDurationMs / 1000), ease: currentPhase.ease as any }
    : null;

  const activeMotion = MOTION_PRESETS[transientConfig?.motionPreset ?? expression.motionPreset];

  const gestureBodyAnim = useMemo(() => {
    if (!activeGesture) return null;
    return GESTURE_EFFECTS[activeGesture]?.bodyEffect || null;
  }, [activeGesture]);

  // ── Baseline-derived visuals ──
  const breathingScale = 1 + baseline.breathingDepth;

  const auraConfig = useMemo(() => {
    const base = {
      ...expression.aura,
      intensity: expression.aura.intensity * (0.5 + baseline.auraBrightness * 0.8),
      pulseSpeed: baseline.auraTension > 0.3
        ? Math.max(expression.aura.pulseSpeed, baseline.pulseRate)
        : expression.aura.pulseSpeed,
      size: expression.aura.size * (0.9 + baseline.auraDensity * 0.3),
      rings: baseline.auraDensity > 0.6 ? Math.max(expression.aura.rings, 2) : expression.aura.rings,
    };
    if (signatureConfig?.auraMod && !activeTransient) {
      base.intensity *= signatureConfig.auraMod.brightnessMultiplier;
      base.pulseSpeed = base.pulseSpeed * signatureConfig.auraMod.tensionMultiplier || base.pulseSpeed;
    }
    return base;
  }, [baseline.auraBrightness, baseline.auraTension, baseline.auraDensity, baseline.pulseRate, expression.aura, signatureConfig, activeTransient]);

  const moodFilter = MOOD_FILTERS[shortTermMood] || MOOD_FILTERS.calm;

  const bodyShape = (signatureConfig?.bodyShapeOverride && !activeTransient)
    ? signatureConfig.bodyShapeOverride
    : (expression.bodyShape || {});
  const bodyScaleX = bodyShape.scaleX ?? 1;
  const bodyScaleY = bodyShape.scaleY ?? 1;

  // ── Scene-aware style ──
  const isCold = seasonState === 'cold';
  const isHot = seasonState === 'heat';
  const isStormy = weather === 'stormy' || weather === 'fee_hurricane';

  const sceneColorGrading = isCold
    ? 'brightness(0.92) saturate(0.85) hue-rotate(-8deg)'
    : isStormy
      ? 'brightness(0.82) saturate(0.9) contrast(1.1) hue-rotate(-5deg)'
      : isHot
        ? 'brightness(1.08) saturate(1.15) hue-rotate(3deg)'
        : 'brightness(1.02) saturate(1.05) hue-rotate(1deg)';

  const shadowColor = isCold
    ? 'hsl(215 30% 20% / 0.50)'
    : isStormy
      ? 'hsl(220 25% 12% / 0.55)'
      : isHot
        ? 'hsl(25 40% 18% / 0.40)'
        : 'hsl(30 25% 15% / 0.38)';

  const shadowWidth = activeSignature === 'laserFlex' ? 150
    : activeSignature === 'crabDeadpan' ? 170
    : shortTermMood === 'hyped' ? 155
    : shortTermMood === 'stressed' ? 170
    : 160;
  const shadowOpacity = activeSignature === 'laserFlex' ? 0.25
    : activeSignature === 'crabDeadpan' ? 0.40
    : shortTermMood === 'hyped' ? 0.28
    : shortTermMood === 'shaken' ? 0.40
    : 0.35;

  return (
    <div className="relative flex flex-col items-center" style={{ minHeight: 280 }}>

      {/* Lightning bolts layer */}
      {lightningVitality > 0.5 && (
        <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center">
          <div className="relative" style={{ width: 200, height: 220 }}>
            <BuddyAura lightningVitality={lightningVitality} />
          </div>
        </div>
      )}

      {/* ── Main body ── */}
      <motion.div
        animate={{ y: 135 + Math.min(baseline.hoverHeight, 2) }}
        transition={{ duration: 2.5, ease: [0.45, 0.05, 0.55, 0.95] }}
        style={{ position: 'relative' }}
      >
        <motion.div
          animate={signatureBodyAnim || gestureBodyAnim || activeMotion.animate}
          transition={signatureTransition
            || (gestureBodyAnim
              ? { duration: (GESTURE_EFFECTS[activeGesture!]?.duration || 1500) / 1000, ease: 'easeInOut' }
              : activeTransient
                ? { ...activeMotion.transition as any, repeat: 0 }
                : activeMotion.transition as any)
          }
          className="relative"
        >
          {/* Breathing */}
          <motion.div
            animate={{
              scale: isCold
                ? [1, breathingScale, 1, 1.005, 0.995, 1.003, 0.997, 1]
                : [1, breathingScale, 1],
            }}
            transition={{
              duration: isCold
                ? Math.max(0.5, (Number.isFinite(baseline.pulseRate) ? baseline.pulseRate : 1.8)) + 0.6
                : Number.isFinite(baseline.pulseRate) ? Math.max(0.5, baseline.pulseRate) : 1.8,
              repeat: Infinity,
              ease: [0.45, 0.05, 0.55, 0.95],
            }}
          >
            {/* Matrix neon glow aura */}
            {matrixMode && (
              <motion.div
                className="absolute inset-0 pointer-events-none rounded-full"
                style={{ zIndex: 4 }}
                animate={{
                  opacity: [0.4, 0.7, 0.4],
                  scale: [1.05, 1.15, 1.05],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <div
                  className="w-full h-full rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(0,255,70,0.15) 0%, rgba(0,255,70,0.06) 40%, transparent 70%)',
                    filter: 'blur(18px)',
                  }}
                />
              </motion.div>
            )}

            {/* Matrix glitch effect */}
            {matrixMode && <MatrixGlitch />}

            {/* Red pill entry glitch burst */}
            {enteringMatrix && (
              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 55 }}>
                {/* Intense RGB split */}
                <div className="absolute inset-0" style={{ transform: 'translateX(6px)', opacity: 0.5, mixBlendMode: 'screen', filter: 'hue-rotate(-40deg) brightness(2) saturate(2)' }} />
                <div className="absolute inset-0" style={{ transform: 'translateX(-6px)', opacity: 0.4, mixBlendMode: 'screen', filter: 'hue-rotate(40deg) brightness(1.8) saturate(2)' }} />
                {/* Rapid horizontal slices */}
                {Array.from({ length: 8 }, (_, i) => {
                  const top = (i * 12) + Math.random() * 5;
                  const tx = (Math.random() - 0.5) * 40;
                  const skew = (Math.random() - 0.5) * 12;
                  return (
                    <div
                      key={i}
                      className="absolute left-0 right-0"
                      style={{
                        top: `${top}%`,
                        height: `${5 + Math.random() * 8}%`,
                        background: `linear-gradient(90deg, transparent 5%, rgba(255,30,30,0.2) 20%, rgba(255,60,60,0.1) 80%, transparent 95%)`,
                        transform: `translateX(${tx}px) skewX(${skew}deg)`,
                        mixBlendMode: 'screen',
                        animation: 'entry-glitch-slice 0.15s ease-in-out infinite alternate',
                      }}
                    />
                  );
                })}
                {/* Red corruption overlay */}
                <div className="absolute inset-0" style={{ background: 'radial-gradient(circle, rgba(255,0,0,0.15) 0%, transparent 70%)', animation: 'entry-glitch-pulse 0.2s ease-in-out infinite alternate' }} />
                <style>{`
                  @keyframes entry-glitch-slice { 0% { opacity: 0.6; transform: translateX(var(--tx, 5px)); } 100% { opacity: 1; transform: translateX(calc(var(--tx, 5px) * -1)); } }
                  @keyframes entry-glitch-pulse { 0% { opacity: 0.3; } 100% { opacity: 0.8; } }
                `}</style>
              </div>
            )}

            {/* Blue pill exit glitch burst */}
            {exitingMatrix && (
              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 55 }}>
                <div className="absolute inset-0" style={{ transform: 'translateX(5px)', opacity: 0.5, mixBlendMode: 'screen', filter: 'hue-rotate(180deg) brightness(2) saturate(2)' }} />
                <div className="absolute inset-0" style={{ transform: 'translateX(-5px)', opacity: 0.4, mixBlendMode: 'screen', filter: 'hue-rotate(220deg) brightness(1.8) saturate(2)' }} />
                {Array.from({ length: 8 }, (_, i) => {
                  const top = (i * 12) + Math.random() * 5;
                  const tx = (Math.random() - 0.5) * 40;
                  const skew = (Math.random() - 0.5) * 12;
                  return (
                    <div
                      key={i}
                      className="absolute left-0 right-0"
                      style={{
                        top: `${top}%`,
                        height: `${5 + Math.random() * 8}%`,
                        background: `linear-gradient(90deg, transparent 5%, rgba(60,130,246,0.2) 20%, rgba(80,150,255,0.1) 80%, transparent 95%)`,
                        transform: `translateX(${tx}px) skewX(${skew}deg)`,
                        mixBlendMode: 'screen',
                        animation: 'exit-glitch-slice 0.15s ease-in-out infinite alternate',
                      }}
                    />
                  );
                })}
                <div className="absolute inset-0" style={{ background: 'radial-gradient(circle, rgba(60,130,246,0.15) 0%, transparent 70%)', animation: 'exit-glitch-pulse 0.2s ease-in-out infinite alternate' }} />
                <style>{`
                  @keyframes exit-glitch-slice { 0% { opacity: 0.6; } 100% { opacity: 1; } }
                  @keyframes exit-glitch-pulse { 0% { opacity: 0.3; } 100% { opacity: 0.8; } }
                `}</style>
              </div>
            )}

            {matrixMode && (
              <motion.div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{ zIndex: 40, mixBlendMode: 'overlay' }}
                animate={{ opacity: [0.03, 0.08, 0.03] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div
                  className="w-full h-full"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,70,0.08) 2px, rgba(0,255,70,0.08) 4px)',
                  }}
                />
              </motion.div>
            )}

            {/* Evolution glow flash */}
            <AnimatePresence>
              {evolving && (
                <motion.div
                  className="absolute inset-0 pointer-events-none rounded-full"
                  style={{ zIndex: 5 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: [0, 0.7, 0], scale: [0.8, 1.3, 1.5] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                  <div
                    className="w-full h-full rounded-full"
                    style={{
                      background: 'radial-gradient(circle, hsl(36 100% 55% / 0.6) 0%, hsl(36 100% 55% / 0.15) 50%, transparent 70%)',
                      filter: 'blur(12px)',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mood-based ambient glow — reflects current state */}
            {!matrixMode && (() => {
              const glowConfig: Record<string, { r: number; g: number; b: number; intensity: number; pulse: number; blur: number }> = {
                calm:        { r: 200, g: 180, b: 130, intensity: 0.06, pulse: 8,   blur: 28 },
                curious:     { r: 210, g: 185, b: 110, intensity: 0.08, pulse: 6,   blur: 26 },
                alert:       { r: 230, g: 140, b: 60,  intensity: 0.14, pulse: 3.5, blur: 22 },
                stressed:    { r: 200, g: 70,  b: 70,  intensity: 0.16, pulse: 2.5, blur: 20 },
                relieved:    { r: 100, g: 200, b: 180, intensity: 0.12, pulse: 5,   blur: 30 },
                hyped:       { r: 255, g: 210, b: 50,  intensity: 0.20, pulse: 2,   blur: 18 },
                shaken:      { r: 150, g: 100, b: 180, intensity: 0.12, pulse: 3,   blur: 24 },
                vibing:      { r: 100, g: 170, b: 220, intensity: 0.10, pulse: 6,   blur: 26 },
                celebrating: { r: 255, g: 185, b: 40,  intensity: 0.22, pulse: 1.8, blur: 16 },
              };
              const g = glowConfig[shortTermMood] || glowConfig.calm;
              const heatBoost = Math.min(0.12, (heatLevel / 100) * 0.12);
              const finalIntensity = g.intensity + heatBoost;

              return (
                <motion.div
                  className="absolute inset-0 pointer-events-none rounded-full"
                  style={{ zIndex: 3 }}
                  animate={{
                    opacity: [finalIntensity * 0.6, finalIntensity, finalIntensity * 0.6],
                    scale: [1.0, 1.08, 1.0],
                  }}
                  transition={{
                    duration: g.pulse,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  <div
                    className="w-full h-full rounded-full"
                    style={{
                      background: `radial-gradient(ellipse at 50% 55%, rgba(${g.r},${g.g},${g.b},${finalIntensity}) 0%, rgba(${g.r},${g.g},${g.b},${finalIntensity * 0.3}) 45%, transparent 75%)`,
                      filter: `blur(${g.blur}px)`,
                    }}
                  />
                </motion.div>
              );
            })()}

            {/* Character layer container */}
            <motion.div
              animate={{
                scaleX: (evolving ? bodyScaleX * 1.08 : bodyScaleX) * faceOffset.renderScale,
                scaleY: (evolving ? bodyScaleY * 1.08 : bodyScaleY) * faceOffset.renderScale,
              }}
              transition={evolving
                ? { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }
                : { duration: 0.6, ease: [0.45, 0.05, 0.55, 0.95] }
              }
              className="relative flex items-center justify-center"
              style={{
                width: 190,
                height: 230,
                filter: matrixMode
                  ? 'drop-shadow(0 0 8px rgba(0,255,70,0.5)) drop-shadow(0 0 20px rgba(0,255,70,0.25)) brightness(1.1) saturate(0.6) hue-rotate(80deg)'
                  : evolving
                    ? `${moodFilter} ${sceneColorGrading} brightness(1.25) drop-shadow(0 0 12px hsl(36 100% 55% / 0.5))`
                    : `${moodFilter} ${sceneColorGrading}`,
                transition: 'filter 0.6s ease',
              }}
            >

              {/* Layer 1: Body (PNG, stage-based evolution) */}
              <LayerImage
                src={bodyStageSrc}
                layerKey={`body-stage-${bodyStage}`}
                zIndex={10}
                duration={0.5}
                priority
              />

              {/* Layer 2: SVG Face overlay (eyes + mouth + blink) — offset per body stage */}
              <AnimatePresence mode="sync">
                <motion.svg
                  key={`face-${layers.eyesKey}-${layers.mouthKey}`}
                  viewBox="0 0 400 500"
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ zIndex: 20, transition: 'transform 0.5s ease' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <g transform={`translate(${faceOffset.svgOriginX}, ${faceOffset.svgOriginY}) scale(${faceOffset.svgScale}) translate(${-faceOffset.svgOriginX}, ${-faceOffset.svgOriginY}) translate(0, ${faceOffset.svgTranslateY})`}>
                    <BuddySvgEyes variant={layers.eyesKey} />
                    <BuddySvgMouth variant={layers.mouthKey} />
                  </g>
                </motion.svg>
              </AnimatePresence>

              {/* Layer 3: Blink overlay (SVG) */}
              <BlinkOverlay
                blinkInterval={baseline.blinkInterval}
                blinkDuration={baseline.blinkDuration}
                doubleBlink={baseline.doubleBlink}
                faceOffset={faceOffset}
              />

              {/* Cold bounce light */}
              {isCold && (
                <div
                  className="absolute z-[35] pointer-events-none"
                  style={{
                    bottom: '0%', left: '15%', width: '70%', height: '35%',
                    background: 'radial-gradient(ellipse 100% 80% at 50% 100%, hsl(210 40% 80% / 0.10) 0%, transparent 70%)',
                    filter: 'blur(8px)',
                  }}
                />
              )}

              {/* Cold rim light */}
              {isCold && (
                <div
                  className="absolute inset-0 z-[35] pointer-events-none"
                  style={{
                    background: 'linear-gradient(180deg, transparent 30%, hsl(210 35% 75% / 0.06) 80%, hsl(210 35% 75% / 0.10) 100%)',
                    maskImage: 'radial-gradient(ellipse 85% 90% at 50% 50%, transparent 60%, black 95%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 85% 90% at 50% 50%, transparent 60%, black 95%)',
                  }}
                />
              )}

            </motion.div>
          </motion.div>

          {/* ── Contact shadows — behind Buddy, offset to back-right ── */}
          <div className="absolute pointer-events-none" style={{
            bottom: '5%',
            left: '50%',
            zIndex: -1,
            transform: 'translateX(-30%) translateY(-10px) skewX(-25deg) scaleY(0.45)',
          }}>
            <div style={{
              width: shadowWidth * 1.2, height: 14,
              background: `radial-gradient(ellipse 100% 100%, ${shadowColor.replace(/[\d.]+\)$/, '0.35)')} 0%, transparent 65%)`,
              filter: 'blur(5px)', borderRadius: '50%',
            }} />
            <motion.div
              animate={{ width: shadowWidth * 1.4, opacity: shadowOpacity }}
              transition={{ duration: 1, ease: 'easeInOut' }}
              style={{
                height: 16, marginTop: -6,
                background: `radial-gradient(ellipse 100% 100%, ${shadowColor} 0%, ${shadowColor.replace(/[\d.]+\)$/, '0.12)')} 50%, transparent 60%)`,
                filter: 'blur(10px)', borderRadius: '50%',
              }}
            />
            <div style={{
              width: shadowWidth * 1.8, height: 14, marginTop: -6,
              background: `radial-gradient(ellipse 100% 100%, ${shadowColor.replace(/[\d.]+\)$/, '0.10)')} 0%, transparent 55%)`,
              filter: 'blur(18px)', borderRadius: '50%',
            }} />
          </div>

        </motion.div>
      </motion.div>

    </div>
  );
}

const BuddyCharacter = React.memo(BuddyCharacterInner);
export default BuddyCharacter;
